import { useState, type FormEvent } from 'react';
import { Key, Save, Trash2, PlayCircle, CheckCircle2, AlertCircle, Loader2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useByokKeys, useAddByok, useTestByok, useDeleteByok } from '@/hooks/useByok';
import { ApiError } from '@/lib/api-client';
import { cn, formatDate } from '@/lib/utils';

const PROVIDERS = [
  {
    id: 'openai' as const,
    label: 'OpenAI',
    placeholder: 'sk-…',
    docs: 'https://platform.openai.com/api-keys',
  },
  {
    id: 'gemini' as const,
    label: 'Google Gemini',
    placeholder: 'AIza…',
    docs: 'https://aistudio.google.com/app/apikey',
  },
];

export function ByokKeysPage() {
  const { data, isPending } = useByokKeys();
  const add = useAddByok();
  const test = useTestByok();
  const del = useDeleteByok();

  const [provider, setProvider] = useState<'openai' | 'gemini'>('openai');
  const [keyVal, setKeyVal] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<{ ok: boolean; text: string } | null>(null);

  const onTest = async () => {
    setError(null);
    setTestResult(null);
    try {
      const res = await test.mutateAsync({ provider, key: keyVal });
      setTestResult({ ok: res.ok, text: res.message });
    } catch (err) {
      setTestResult({ ok: false, text: err instanceof ApiError ? err.message : 'Test başarısız' });
    }
  };

  const onAdd = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await add.mutateAsync({ provider, key: keyVal, label: label || undefined });
      setKeyVal('');
      setLabel('');
      setTestResult(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Eklenemedi');
    }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Key className="w-5 h-5" /> Kendi API Keylerim (BYOK)
        </h1>
        <p className="text-muted-foreground text-sm">
          OpenAI veya Gemini hesabının API key'ini buraya ekle — AI çağrılarında senin hesabın kullanılır,
          platform kredisi harcanmaz. Key'ler AES-GCM ile şifrelenip saklanır.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Yeni Key Ekle</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onAdd} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="prov">Sağlayıcı</Label>
                <select
                  id="prov"
                  value={provider}
                  onChange={(e) => setProvider(e.target.value as 'openai' | 'gemini')}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  {PROVIDERS.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
                <a
                  href={PROVIDERS.find((p) => p.id === provider)!.docs}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                >
                  Key nasıl alınır? <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <div className="space-y-2">
                <Label htmlFor="lbl">Etiket (opsiyonel)</Label>
                <Input id="lbl" value={label} onChange={(e) => setLabel(e.target.value)} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="key">API Key</Label>
              <Input
                id="key"
                type="password"
                autoComplete="off"
                value={keyVal}
                onChange={(e) => setKeyVal(e.target.value)}
                placeholder={PROVIDERS.find((p) => p.id === provider)!.placeholder}
                required
              />
            </div>
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 rounded-md border border-destructive/30 px-3 py-2">
                {error}
              </div>
            )}
            {testResult && (
              <div
                className={cn(
                  'text-xs rounded px-3 py-2 flex items-start gap-1.5',
                  testResult.ok
                    ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400'
                    : 'bg-destructive/10 text-destructive',
                )}
              >
                {testResult.ok ? (
                  <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                ) : (
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                )}
                {testResult.text}
              </div>
            )}
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={onTest}
                disabled={keyVal.length < 8 || test.isPending}
              >
                {test.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
                Test Et
              </Button>
              <Button type="submit" disabled={keyVal.length < 8 || add.isPending}>
                {add.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Kaydet
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {!isPending && data && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Kayıtlı Keyler ({data.keys.length})</CardTitle>
            <CardDescription>
              Birden fazla key ekleyebilirsin. En son eklenen öncelikli olarak kullanılır.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {data.keys.length === 0 ? (
              <p className="text-sm text-muted-foreground">Henüz key yok.</p>
            ) : (
              <div className="space-y-2">
                {data.keys.map((k) => (
                  <div
                    key={k.id}
                    className="flex items-center justify-between rounded-md border bg-muted/20 px-3 py-2"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <span className="font-medium text-sm">
                        {k.provider === 'openai' ? 'OpenAI' : 'Gemini'}
                      </span>
                      <code className="font-mono text-xs text-muted-foreground">{k.key_hint}</code>
                      {k.label && <span className="text-xs text-muted-foreground truncate">{k.label}</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground hidden sm:inline">
                        {formatDate(k.created_at)}
                      </span>
                      <Button size="sm" variant="ghost" onClick={() => del.mutate(k.id)} disabled={del.isPending}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
